import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Divider,
  FormHelperText,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays, differenceInDays } from 'date-fns';
import id from 'date-fns/locale/id';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { t } from '../../../../utils/textUtils';

// NOTE: Copied from legacy TaskSplitDialog to ensure full parity with old feature
const TaskSplitDialog = ({ open, onClose, task, onSplit }) => {
  const [splitDate, setSplitDate] = useState(null);
  const [resumeDate, setResumeDate] = useState(null);
  const [task2EndDate, setTask2EndDate] = useState(null);
  const [task1Progress, setTask1Progress] = useState(0);
  const [task2Progress, setTask2Progress] = useState(0);
  const [task1Quantity, setTask1Quantity] = useState(0);
  const [task2Quantity, setTask2Quantity] = useState(0);
  const [reason, setReason] = useState('');
  const [namingFormat, setNamingFormat] = useState('fase'); // 'fase', 'split', 'bagian', 'status'
  const [errors, setErrors] = useState({});

  // Helper function to calculate progress based on quantity
  const calculateProgressFromQuantity = (quantity, totalQuantity) => {
    if (totalQuantity === 0) return 0;
    return Math.round((quantity / totalQuantity) * 100);
  };

  // Helper function to auto-sync progress with quantity
  const syncProgressWithQuantity = () => {
    const totalQuantity = task?.quantity || 0;
    if (totalQuantity > 0) {
      const calculatedTask1Progress = calculateProgressFromQuantity(task1Quantity, totalQuantity);
      const calculatedTask2Progress = calculateProgressFromQuantity(task2Quantity, totalQuantity);
      // Only update if significantly different (to avoid infinite loops)
      if (Math.abs(calculatedTask1Progress - task1Progress) > 5) {
        setTask1Progress(calculatedTask1Progress);
      }
      if (Math.abs(calculatedTask2Progress - task2Progress) > 5) {
        setTask2Progress(calculatedTask2Progress);
      }
    }
  };

  // Helper function to generate task names based on format
  const generateTaskNames = (format = namingFormat) => {
    // Use the explicit task.name field for split naming, fallback to stripping quantity from text
    const baseName = task?.name || task?.text?.replace(/\s\(\d+.*\)$/, '') || 'Task';
    
    switch (format) {
      case 'fase':
        return {
          task1Name: `${baseName} - Fase 1`,
          task2Name: `${baseName} - Fase 2`
        };
      case 'split':
        return {
          task1Name: `${baseName} - ${t('SPLIT')} 1`,
          task2Name: `${baseName} - ${t('SPLIT')} 2`
        };
      case 'bagian':
        return {
          task1Name: `${baseName} - ${t('BAGIAN')} 1`,
          task2Name: `${baseName} - ${t('BAGIAN')} 2`
        };
      case 'status':
        return {
          task1Name: `[${t('SELESAI')}] ${baseName}`,
          task2Name: `[${t('LANJUTAN')}] ${baseName}`
        };
      default:
        return {
          task1Name: `${baseName} - Fase 1`,
          task2Name: `${baseName} - Fase 2`
        };
    }
  };

  // Reset form when dialog opens
  useEffect(() => {
    if (open && task) {
      // Default split date to middle of task duration
      const startDate = new Date(task.start_date);
      const endDate = new Date(task.end_date);
      const totalDays = differenceInDays(endDate, startDate);
      const middleDate = addDays(startDate, Math.floor(totalDays / 2));
      
      setSplitDate(middleDate);
      setResumeDate(addDays(endDate, 1)); // Default resume day after original end date
      setTask2EndDate(addDays(endDate, 7)); // Default: 1 week after original end date
      setTask1Progress(100); // Default: task 1 is completed (100%)
      setTask2Progress(0); // Task 2 starts from 0%
      
      // Default quantity split (50-50 or based on current progress)
      const totalQuantity = task.quantity || 0;
      const currentProgress = (task.progress || 0) * 100;
      
      if (currentProgress > 0) {
        // If there's existing progress, use it as basis for task1 quantity
        const task1Qty = Math.round((currentProgress / 100) * totalQuantity);
        setTask1Quantity(task1Qty);
        setTask2Quantity(totalQuantity - task1Qty);
      } else {
        // Default 50-50 split
        const halfQuantity = Math.floor(totalQuantity / 2);
        setTask1Quantity(halfQuantity);
        setTask2Quantity(totalQuantity - halfQuantity);
      }
      
      setReason('');
      setErrors({});
    }
  }, [open, task]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!splitDate) {
      newErrors.splitDate = t('TANGGAL SPLIT WAJIB DIISI');
    } else {
      const taskStart = new Date(task.start_date);
      const taskEnd = new Date(task.end_date);
      
      if (splitDate <= taskStart) {
        newErrors.splitDate = t('TANGGAL SPLIT HARUS SETELAH TANGGAL MULAI TASK');
      }
      
      if (splitDate >= taskEnd) {
        newErrors.splitDate = t('TANGGAL SPLIT HARUS SEBELUM TANGGAL SELESAI TASK');
      }
    }
    
    if (!resumeDate) {
      newErrors.resumeDate = t('TANGGAL LANJUT WAJIB DIISI');
    } else if (splitDate && resumeDate <= splitDate) {
      newErrors.resumeDate = t('TANGGAL LANJUT HARUS SETELAH TANGGAL SPLIT');
    }
    
    if (!task2EndDate) {
      newErrors.task2EndDate = t('TANGGAL SELESAI TASK 2 WAJIB DIISI');
    } else if (resumeDate && task2EndDate <= resumeDate) {
      newErrors.task2EndDate = t('TANGGAL SELESAI HARUS SETELAH TANGGAL MULAI TASK 2');
    }
    
    if (task1Progress < 0 || task1Progress > 100) {
      newErrors.task1Progress = t('PROGRESS TASK 1 HARUS ANTARA 0-100%');
    }
    
    if (task2Progress < 0 || task2Progress > 100) {
      newErrors.task2Progress = t('PROGRESS TASK 2 HARUS ANTARA 0-100%');
    }
    
    // Validate quantities
    const totalQuantity = task.quantity || 0;
    
    if (task1Quantity < 0) {
      newErrors.task1Quantity = t('QUANTITY TASK 1 TIDAK BOLEH NEGATIF');
    }
    
    if (task2Quantity < 0) {
      newErrors.task2Quantity = t('QUANTITY TASK 2 TIDAK BOLEH NEGATIF');
    }
    
    if (task1Quantity + task2Quantity !== totalQuantity) {
      newErrors.quantityTotal = `${t('TOTAL QUANTITY HARUS SAMA DENGAN QUANTITY ASLI')} (${totalQuantity} ${task.unit || t('UNIT')})`;
    }
    
    if (!reason.trim()) {
      newErrors.reason = t('ALASAN SPLIT WAJIB DIISI');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle split
  const handleSplit = () => {
    if (!validateForm()) return;
    
    const taskNames = generateTaskNames();
    
    const splitData = {
      originalTask: task,
      splitDate,
      resumeDate,
      task2EndDate,
      task1Progress: task1Progress / 100, // Convert back to decimal
      task2Progress: task2Progress / 100, // Convert back to decimal
      task1Quantity,
      task2Quantity,
      reason: reason.trim(),
      // Task names (dynamic based on selected format)
      task1Name: taskNames.task1Name,
      task2Name: taskNames.task2Name,
      namingFormat, // Send format info to backend
      // Calculate durations
      firstTaskDuration: differenceInDays(splitDate, new Date(task.start_date)) + 1,
      secondTaskDuration: differenceInDays(task2EndDate, resumeDate) + 1
    };
    
    onSplit(splitData);
    onClose();
  };

  // Calculate split information
  const getSplitInfo = () => {
    if (!task || !splitDate || !resumeDate || !task2EndDate) return null;
    
    const taskStart = new Date(task.start_date);
    const taskEnd = new Date(task.end_date);
    const originalDuration = differenceInDays(taskEnd, taskStart) + 1;
    const firstDuration = differenceInDays(splitDate, taskStart) + 1;
    const secondDuration = differenceInDays(task2EndDate, resumeDate) + 1;
    const gapDuration = differenceInDays(resumeDate, splitDate) - 1;
    
    return {
      originalDuration,
      firstDuration,
      secondDuration,
      gapDuration,
      firstTaskEnd: splitDate,
      secondTaskStart: resumeDate,
      secondTaskEnd: task2EndDate
    };
  };

  const splitInfo = getSplitInfo();

  if (!task) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={id}>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SplitscreenIcon color="primary" />
            <Typography variant="h6">{t('SPLIT TASK')}</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {/* Task Information */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t('TASK YANG AKAN DI-SPLIT')}:
            </Typography>
            <Typography variant="body2">
              <strong>{task.text}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('PERIODE')}: {format(new Date(task.start_date), 'dd MMM yyyy', { locale: id })} - {format(new Date(task.end_date), 'dd MMM yyyy', { locale: id })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('PROGRESS SAAT INI')}: {Math.round((task.progress || 0) * 100)}%
            </Typography>
          </Alert>

          {/* Usage Example */}
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              💡 {t('CONTOH PENGGUNAAN')}:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>{t('TASK 1')} (100%):</strong> {t('PEKERJAAN YANG SUDAH SELESAI SAMPAI TANGGAL SPLIT')}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • <strong>{t('TASK 2')} (0%):</strong> {t('PEKERJAAN YANG AKAN DILANJUTKAN DI TANGGAL RESUME')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('TANGGAL RESUME BISA DIMUNDURKAN SESUAI KEBUTUHAN JADWAL PROYEK')}
            </Typography>
          </Alert>

          <Grid container spacing={3}>
            {/* Split Configuration */}
            <Grid size={{ xs: 12, md: 12 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon />
                {t('KONFIGURASI SPLIT')}
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label={t('TANGGAL SPLIT (AKHIR TASK PERTAMA)')}
                value={splitDate}
                onChange={setSplitDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.splitDate,
                    helperText: errors.splitDate || t('TANGGAL TERAKHIR PENGERJAAN SEBELUM DIHENTIKAN'),
                    required: true,
                  }
                }}
                minDate={addDays(new Date(task.start_date), 1)}
                maxDate={addDays(new Date(task.end_date), -1)}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label={t('TANGGAL RESUME (BISA DIMUNDURKAN)')}
                value={resumeDate}
                onChange={setResumeDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.resumeDate,
                    helperText: errors.resumeDate || t('TANGGAL MULAI TASK KEDUA - BISA DIMUNDURKAN SESUAI KEBUTUHAN'),
                    required: true,
                  }
                }}
                minDate={splitDate ? addDays(splitDate, 1) : new Date()}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <DatePicker
                label={t('TANGGAL SELESAI TASK 2')}
                value={task2EndDate}
                onChange={setTask2EndDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.task2EndDate,
                    helperText: errors.task2EndDate || t('TANGGAL TARGET SELESAI UNTUK TASK KEDUA'),
                    required: true,
                  }
                }}
                minDate={resumeDate ? addDays(resumeDate, 1) : new Date()}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('PROGRESS TASK 1 (SUDAH SELESAI)')}
                type="number"
                value={task1Progress}
                onChange={(e) => setTask1Progress(Number(e.target.value))}
                fullWidth
                error={!!errors.task1Progress}
                helperText={errors.task1Progress || t('PROGRESS YANG SUDAH DICAPAI SAMPAI TANGGAL SPLIT (BIASANYA 100%)')}
                InputProps={{
                  endAdornment: '%'
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 1
                }}
                required
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('PROGRESS TASK 2 (MULAI DARI)')}
                type="number"
                value={task2Progress}
                onChange={(e) => setTask2Progress(Number(e.target.value))}
                fullWidth
                error={!!errors.task2Progress}
                helperText={errors.task2Progress || t('PROGRESS AWAL SAAT TASK DILANJUTKAN (BIASANYA 0%)')}
                InputProps={{
                  endAdornment: '%'
                }}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: 1
                }}
                required
              />
            </Grid>

            {/* Quantity Fields */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  📊 {t('PEMBAGIAN QUANTITY')}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={syncProgressWithQuantity}
                  sx={{ mt: 1 }}
                >
                  🔄 {t('SYNC PROGRESS DENGAN QUANTITY')}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('TOTAL QUANTITY ASLI')}: <strong>{task?.quantity || 0} {task?.unit || t('UNIT')}</strong>
              </Typography>
              {errors.quantityTotal && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.quantityTotal}
                </Alert>
              )}
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('QUANTITY TASK 1 (SUDAH DIKERJAKAN)')}
                type="number"
                value={task1Quantity}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setTask1Quantity(value);
                  // Auto-adjust task2 quantity
                  const totalQty = task?.quantity || 0;
                  setTask2Quantity(totalQty - value);
                }}
                fullWidth
                error={!!errors.task1Quantity}
                helperText={errors.task1Quantity || t('QUANTITY YANG SUDAH DIKERJAKAN SAMPAI TANGGAL SPLIT')}
                InputProps={{
                  endAdornment: task?.unit || t('UNIT')
                }}
                inputProps={{
                  min: 0,
                  max: task?.quantity || 0,
                  step: 0.01
                }}
                required
              />
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('QUANTITY TASK 2 (SISA PEKERJAAN)')}
                type="number"
                value={task2Quantity}
                onChange={(e) => {
                  const value = Number(e.target.value);
                  setTask2Quantity(value);
                  // Auto-adjust task1 quantity
                  const totalQty = task?.quantity || 0;
                  setTask1Quantity(totalQty - value);
                }}
                fullWidth
                error={!!errors.task2Quantity}
                helperText={errors.task2Quantity || t('QUANTITY YANG AKAN DIKERJAKAN DI TASK KEDUA')}
                InputProps={{
                  endAdornment: task?.unit || t('UNIT')
                }}
                inputProps={{
                  min: 0,
                  max: task?.quantity || 0,
                  step: 0.01
                }}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>{t('FORMAT PENAMAAN TASK')}</InputLabel>
                <Select
                  value={namingFormat}
                  onChange={(e) => setNamingFormat(e.target.value)}
                  label={t('FORMAT PENAMAAN TASK')}
                >
                  <MenuItem value="fase">{t('FASE')} 1 / {t('FASE')} 2</MenuItem>
                  <MenuItem value="split">{t('SPLIT')} 1 / {t('SPLIT')} 2</MenuItem>
                  <MenuItem value="bagian">{t('BAGIAN')} 1 / {t('BAGIAN')} 2</MenuItem>
                  <MenuItem value="status">[{t('SELESAI')}] / [{t('LANJUTAN')}]</MenuItem>
                </Select>
                <FormHelperText>{t('PILIH FORMAT PENAMAAN UNTUK TASK HASIL SPLIT')}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label={t('ALASAN SPLIT')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
                error={!!errors.reason}
                helperText={errors.reason || t('ALASAN MENGAPA TASK PERLU DI-SPLIT')}
                placeholder={t('CONTOH: PRIORITAS BERPINDAH KE TASK LAIN')}
                required
              />
            </Grid>

            {/* Split Preview */}
            {splitInfo && (
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PlayArrowIcon />
                  {t('PREVIEW HASIL SPLIT')}
                </Typography>
                
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          {t('TASK PERTAMA (SUDAH DIKERJAKAN)')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('NAMA')}:</strong> {generateTaskNames().task1Name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('PERIODE')}:</strong> {format(new Date(task.start_date), 'dd MMM', { locale: id })} - {format(splitDate, 'dd MMM yyyy', { locale: id })}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('DURASI')}:</strong> {splitInfo.firstDuration} {t('HARI')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('PROGRESS')}:</strong> {task1Progress}%
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('QUANTITY')}:</strong> {task1Quantity} {task?.unit || t('UNIT')}
                        </Typography>
                        <Chip label={t('COMPLETED')} color="success" size="small" sx={{ mt: 1 }} />
                      </Box>
                    </Grid>
                    
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                        <Typography variant="subtitle2" color="warning.dark" gutterBottom>
                          {t('TASK KEDUA (AKAN DILANJUTKAN)')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('NAMA')}:</strong> {generateTaskNames().task2Name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('PERIODE')}:</strong> {format(resumeDate, 'dd MMM', { locale: id })} - {format(task2EndDate, 'dd MMM yyyy', { locale: id })}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('DURASI')}:</strong> {splitInfo.secondDuration} {t('HARI')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('PROGRESS')}:</strong> {task2Progress}% → 100%
                        </Typography>
                        <Typography variant="body2">
                          <strong>{t('QUANTITY')}:</strong> {task2Quantity} {task?.unit || t('UNIT')}
                        </Typography>
                        <Chip label={t('PENDING')} color="warning" size="small" sx={{ mt: 1 }} />
                      </Box>
                    </Grid>
                    
                    {splitInfo.gapDuration > 0 && (
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>{t('GAP PERIOD')}:</strong> {splitInfo.gapDuration} {t('HARI')} {t('ANTARA')} {format(addDays(splitDate, 1), 'dd MMM', { locale: id })} - {format(addDays(resumeDate, -1), 'dd MMM yyyy', { locale: id })}
                          </Typography>
                          <Typography variant="body2">
                            {t('TASK AKAN DIHENTIKAN SEMENTARA SELAMA PERIODE INI.')}
                          </Typography>
                        </Alert>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={onClose} color="inherit">
            {t('BATAL')}
          </Button>
          <Button 
            onClick={handleSplit} 
            variant="contained" 
            color="primary"
            startIcon={<SplitscreenIcon />}
            disabled={!splitDate || !resumeDate || !task2EndDate}
          >
            {t('SPLIT TASK')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default TaskSplitDialog;
